import { useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Translate } from 'react-redux-i18n'
import { UseI18n } from '../../../../../common/hooks/useI18n'
import { Element } from '../../../../../common/html/element'
import { Icon } from '../../../../../common/html/icon'
import { Tooltip } from '../../../../../common/tooltip'
import { COMPANY_TYPES } from '../../../../../utils/common'
import {
  CRITERIA_GROUP,
  INDICATOR_ID,
  INDICATOR_TYPE,
} from '../../../common/constant'
import { getCurrentTime } from '../../../common/helper'
import { selectScreeningIndicatorById } from '../../../common/store/slice'
import { changeCriteria, selectCriteria } from '../../store/slice'
import { DateTimeRange } from '../inputs/datetimeRange'
import { DropdownList } from '../inputs/dropdownList'
import { Location } from '../inputs/location'
import { NumberRange } from '../inputs/numberRange'
import { RadioGroup } from '../inputs/radio'
import { TextArea } from '../inputs/textArea'
import { YearNumberRange } from '../inputs/yearNumberRange'
import classes from './index.module.scss'
import { PopupImportFile } from './PopupImportFile'

const screeningIndicatorCanImportFile = [3]

export const CriteriaIndicator = ({ criteriaGroup, screeningIndicatorId }) => {
  const dispatch = useDispatch()

  const screeningIndicatorName = useSelector(
    selectScreeningIndicatorById(
      screeningIndicatorId,
      'screeningIndicatorName',
    ),
  )
  const comTypeCode = useSelector(
    selectScreeningIndicatorById(screeningIndicatorId, 'comTypeCode'),
  )
  const indicatorType = useSelector(
    selectScreeningIndicatorById(screeningIndicatorId, 'indicatorType'),
  )
  const conditionIds = useSelector(
    selectCriteria([criteriaGroup, screeningIndicatorId, 'data'], (data) => {
      if (!data) return {}
      return Object.keys(data)
        .filter((key) => !!data[key]?.value)
        .reduce((obj, key) => ({ ...obj, [key]: data[key] }), {})
    }),
  )

  const [isShowImport, setIsShowImport] = useState(false)

  const isImportFile = useMemo(() => {
    return (
      criteriaGroup === CRITERIA_GROUP.BASIC_INFORMATION &&
      screeningIndicatorCanImportFile.includes(screeningIndicatorId)
    )
  }, [criteriaGroup, screeningIndicatorId])

  const renderComType = () => {
    switch (comTypeCode) {
      case COMPANY_TYPES.CORPORATE:
        return `(${UseI18n('screening.CORPORATE')})`
      case COMPANY_TYPES.BANK:
        return `(${UseI18n('screening.BANK')})`
      case COMPANY_TYPES.SECURITIES:
        return `(${UseI18n('screening.SECURITIES')})`
      case COMPANY_TYPES.INSURANCE:
        return `(${UseI18n('screening.INSURANCE')})`
      default:
        return ''
    }
  }

  const renderRadioCondition = () => {
    return (
      <Element className="flex1 ml5">
        <RadioGroup
          criteriaGroup={criteriaGroup}
          screeningIndicatorId={screeningIndicatorId}
        />
      </Element>
    )
  }

  const renderConditions = () => {
    const props = {
      criteriaGroup,
      screeningIndicatorId,
    }

    switch (indicatorType) {
      case INDICATOR_TYPE.Text:
        return null
      case INDICATOR_TYPE.TextBox:
        return <TextArea {...props} />
      case INDICATOR_TYPE.Number:
        return <TextArea isNumber {...props} />
      case INDICATOR_TYPE.DateTime:
        return null
      case INDICATOR_TYPE.NumberRange:
        return <NumberRange {...props} />
      case INDICATOR_TYPE.FloatRange:
        return <NumberRange isFloatNumber {...props} />
      case INDICATOR_TYPE.DateTimeRange:
        return <DateTimeRange {...props} />
      case INDICATOR_TYPE.DropdownList:
        return <DropdownList {...props} />
      case INDICATOR_TYPE.MultipleDropdownList:
        if (screeningIndicatorId !== INDICATOR_ID.LOCATION) return null
        return <Location {...props} />
      case INDICATOR_TYPE.FinancialRange:
        return <YearNumberRange allowNegativeNumber {...props} />
      case INDICATOR_TYPE.RatioRange:
        return <YearNumberRange isFloatNumber {...props} />
      case INDICATOR_TYPE.Tree:
        return null
      case INDICATOR_TYPE.Radio:
        return null
      default:
        return null
    }
  }

  const handleDeleteIndicator = () => {
    dispatch(
      changeCriteria({
        path: [criteriaGroup, screeningIndicatorId],
        data: undefined,
      }),
    )
  }

  const handleApplyImport = (data) => {
    const id = getCurrentTime()
    const newValue = data.reduce(
      (obj, item, index) => ({
        ...obj,
        [id + index]: { id: id + index, value: Object.values(item).join('') },
      }),
      {},
    )
    dispatch(
      changeCriteria({
        path: [criteriaGroup, screeningIndicatorId, 'data'],
        data: { ...conditionIds, ...newValue },
      }),
    )
  }

  return (
    <>
      <Element
        className={classes.filterItem}
        style={{ borderColor: '#D5D5D5' }}
      >
        <Element
          className={classes.header}
          style={{
            borderBottom:
              indicatorType === INDICATOR_TYPE.Radio ? 'none' : '1px dashed',
            borderBottomColor: '#D5D5D5',
          }}
        >
          <Element className={classes.filterName} style={{ color: '#3A414B' }}>
            {screeningIndicatorName} {renderComType()}
            {screeningIndicatorId === INDICATOR_ID.HAS_INDEPENDENT_COMPANY && (
              <Tooltip
                tooltipContent="screening.INDEPENDENT_COMPANY_TOOLTIP"
                isI18n
                translateProps={{ dangerousHTML: true }}
                contentStyle={{ display: 'flex', alignItems: 'center' }}
              >
                <Icon
                  className="icon-info-circle ml1"
                  style={{ fontSize: 14, color: '#9c9c9c', cursor: 'pointer' }}
                />
              </Tooltip>
            )}
          </Element>
          {indicatorType === INDICATOR_TYPE.Radio && renderRadioCondition()}
          <Element className="dFlex alignItemsCenter">
            {isImportFile && (
              <Element
                elementName="p"
                style={{
                  color: '#2C73E8',
                  lineHeight: '28px',
                  cursor: 'pointer',
                }}
                className="whiteSpaceNowrap mr1"
                onClick={() => setIsShowImport(true)}
              >
                <Translate value="common.IMPORT_FILE" />
              </Element>
            )}
            <Icon
              className="icon-trash-fill"
              style={{ color: '#9C9C9C', fontSize: 14 }}
              onClick={handleDeleteIndicator}
            />
          </Element>
        </Element>

        {indicatorType !== INDICATOR_TYPE.Radio && (
          <Element className={classes.conditions}>{renderConditions()}</Element>
        )}
      </Element>
      {isShowImport && (
        <PopupImportFile
          isShow={isShowImport}
          setShow={setIsShowImport}
          onApply={handleApplyImport}
        />
      )}
    </>
  )
}
